/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { StorageModule } from "@vlinder-web/storage-module-react";

import { CREATE_MULTI_BOX } from "../graphql/createMultiBox.mutation";

const log: any = LoggerModule?.getInstance();
const storageService = StorageModule.getInstance();

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICreateMultiBoxCampMethods extends IPluginMethodMap {
  CreateMultiBoxCamp(args: any): Promise<any>;
}

export class CreateMultiBoxCampService implements IAgentPlugin {
  readonly methods: ICreateMultiBoxCampMethods;

  constructor() {
    this.methods = {
      CreateMultiBoxCamp: this.CreateMultiBoxCamp.bind(this),
    };
  }

  async CreateMultiBoxCamp(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        let variables: any = {
          data: this.transformData(args?.createMulti || args?.items),
        };
        const res = await client.mutate({
          mutation: CREATE_MULTI_BOX,
          variables: variables,
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          // Success feedback is shown via ShopUpsellHost modal on Orders
          return res?.data;
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find(
          (item: any) => item?.extensions?.display_message
        )?.extensions?.display_message;

      const errorMessage =
        _display_message ||
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.message ||
        err?.message ||
        "Some unexpected error occurred";

      throw Error(errorMessage);
    }
  }

  private transformData(data?: any) {
    return (data || []).map((entry?: any) => {
      const { selectedDiscount, items } = entry || {};
      const discountCode =
        selectedDiscount ||
        (!Array.isArray(items)
          ? items?.selectedDiscount || items?.discountCode
          : undefined) ||
        entry?.discountCode ||
        entry?.discount ||
        null;

      const rawItems = Array.isArray(items)
        ? items
        : Array.isArray(items?.items)
        ? items.items
        : Array.isArray(entry?.items)
        ? entry.items
        : [];

      const mappedItems = rawItems.map((item: any) => {
        const costumeType = item?.costumeType?.id ?? item?.costumeType;
        const costumeFeature = item?.costumeFeature?.id ?? item?.costumeFeature;
        const costumeAttribute =
          item?.costumeAttribute?.id ?? item?.costumeAttribute;
        const costumeValue = item?.costumeValue?.id ?? item?.costumeValue;

        return {
          costumeType,
          costumeFeature,
          costumeAttribute,
          costumeValue,
        };
      });

      const presentation =
        entry?.presentation?.id ??
        (typeof entry?.presentation === "string"
          ? entry.presentation
          : undefined) ??
        entry?.presentationID ??
        entry?.presentationId ??
        (!Array.isArray(items)
          ? items?.presentation?.id ??
            (typeof items?.presentation === "string"
              ? items?.presentation
              : undefined)
          : undefined) ??
        entry?.content?.presentation?.id ??
        (typeof entry?.content?.presentation === "string"
          ? entry.content?.presentation
          : undefined) ??
        entry?.content?.presentationID ??
        entry?.content?.presentationId ??
        entry?.payOrder?.box?.[0]?.presentation?.id ??
        (typeof entry?.payOrder?.box?.[0]?.presentation === "string"
          ? entry.payOrder?.box?.[0]?.presentation
          : undefined);

      const section =
        entry?.section?.id ??
        (typeof entry?.section === "string" ? entry.section : undefined) ??
        entry?.sectionID ??
        entry?.sectionId ??
        (!Array.isArray(items)
          ? items?.section?.id ??
            (typeof items?.section === "string" ? items?.section : undefined)
          : undefined) ??
        entry?.content?.section?.id ??
        (typeof entry?.content?.section === "string"
          ? entry.content?.section
          : undefined) ??
        entry?.content?.sectionID ??
        entry?.content?.sectionId ??
        entry?.payOrder?.box?.[0]?.section?.id ??
        (typeof entry?.payOrder?.box?.[0]?.section === "string"
          ? entry.payOrder?.box?.[0]?.section
          : undefined);

      const referal =
        entry?.referal ??
        entry?.referalID ??
        entry?.referalId ??
        (!Array.isArray(items) ? items?.referal : undefined) ??
        entry?.content?.referal ??
        entry?.content?.referalID ??
        entry?.content?.referalId ??
        entry?.payOrder?.box?.[0]?.referal;

      const transformedEntry: any = {
        presentation,
        section,
        referal,
        items: mappedItems,
      };

      if (discountCode) {
        transformedEntry.discountCode = discountCode;
      }

      if (entry?.availableTime || items?.availableTime) {
        transformedEntry.availableTime =
          entry?.availableTime || items?.availableTime;
      }
      if (entry?.address || items?.address) {
        transformedEntry.address = entry?.address || items?.address;
      } else {
        transformedEntry.address = { addressLine1: null, city: null };
      }
      if (
        entry?.deliveryOpted ||
        items?.deliveryOpted ||
        items?.delivery?.isOpted
      ) {
        transformedEntry.delivery = { isOpted: true };
      } else {
        transformedEntry.delivery = { isOpted: false };
      }
      return transformedEntry;
    });
  }
}
